<template>
  <b-container class="detail-field-catalog-section mb-4">
    <b-row class="detail-field-catalog-section-header tabHeader mb-2">
      <b-col cols="12" class="font-weight-bold">{{ title }}</b-col>
    </b-row>
    <slot></slot>
  </b-container>
</template>

<script>
// The shared sidebar "card" shell (border/background/radius/header), used by FieldCatalogSection.vue
// and by any typed component needing the same box treatment (e.g.
// typedSections/LiquiditySection.vue, which renders a slider instead of label/value rows).
// Gotchas baked into the CSS below: no-gutters breaks the gutter cancellation, and the header row
// needs a `margin: auto` override. Extend this file rather than re-deriving the CSS.
import { detailSectionTitle } from '../../utils/detailFieldFormat'

export default {
  name: 'detail-card',
  props: {
    sectionKey: { type: String, required: true },
  },
  computed: {
    title() {
      return detailSectionTitle(this, this.sectionKey)
    },
  },
}
</script>

<style lang="scss" scoped>
.detail-field-catalog-section {
  border: 1px solid #e5e7eb;
  background-color: white;
  border-radius: 6px;
}

// Generic — deliberately NOT scoped to any one consumer's own row class (`.detail-field-row` or
// similar): the rule applies uniformly to every `.row` under the box
// regardless of which child component rendered it (ProductCharacteristics.vue's, MarketIndices.vue's,
// LiquidityValue.vue's — all get the exact same spacing). `/deep/` is required here because these
// rows are slotted-in content authored by a DIFFERENT component (FieldCatalogSection.vue,
// LiquiditySection.vue, ...) — without it, Vue's scoped-CSS attribute would never match them, same
// reasoning as the header's own card treatment below. Content rows always land as siblings starting
// at child 2 (the header row is always child 1), so `:nth-child(2)` correctly targets "the first
// content row" — not `:first-child`, which would never match here.
//
// Ordering matters: this block MUST come before .detail-field-catalog-section-header below. Both
// `/deep/ .row` and `.detail-field-catalog-section-header` compile to equal specificity (one
// scope-attribute + one class each), and the header row itself also carries the plain `.row` class
// (every b-row does) — so both rules match it simultaneously. At a specificity tie, the LATER rule in
// source order wins — reversing the order here would let this 0.5rem bottom margin leak
// into the header instead of the header's own `margin: auto` cleanly winning.
/deep/ .row {
  margin-bottom: 0.5rem;
}

/deep/ .row:nth-child(2) {
  margin-top: 2rem;
}

/deep/ .row:last-child {
  margin-bottom: 2rem;
}

.detail-field-catalog-section-header {
  font-weight: 700 !important;
  background-color: white;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  border-top-left-radius: 0.5rem;
  border-top-right-radius: 0.5rem;
  border-bottom: 1px solid var(--gray4);
  // Overrides bootstrap's own .row default (margin-left/-right: -15px, which cancels the
  // container's padding) back to 0 — same as ProductDetails.vue's own `/deep/ ...tabHeader { margin:
  // auto; ... }`. Net effect: the header text sits at container-padding (15px) + col-gutter (15px) =
  // 30px inset, deliberately more than the 15px inset a slotted-in content row gets below (its own
  // -15px margin cancels the container's padding back down). Must stay AFTER the generic `/deep/
  // .row` block above — see the ordering note there.
  margin: auto;
}
</style>
